import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient} from "@angular/common/http";
import { environment } from '@environments/environment';
import { DashboardMeasureDetailProd } from '@models/dashboard-measure-details.model';
import { DashboardMeasureLocation } from '@models/dashboard-location.model';
import { DashboardFilterRequest } from '@models/dashboard-filter.model';
import { DashboardMeasurePatientProd } from '@models/dashboard-measure-patient.model';
import { PatientValidationRemovalResponse, PatientValidationRequest, PatientValidationResponse, RemovePatientValidationRequest } from '@models/patient-validation.model';
import { DashboardMeasureToolPatientResult, DashboardMeasureToolMetaDataResult } from '@models/measure-tool.model';
import { DashboardMipsScoreRequest, DashboardMipsScoreResponse } from '@models/dashboard-mips-scores.model';
import { DashboardMeasureDecile } from '@models/dashboard-measure-deciles.model';
import { CalculateMipsReweightingRequest, CalculateMipsReweightingResponse } from '@models/mips-reweighting-score.model';


@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private apiUrl = environment.apiUrl;

  getMeasureSummaries(orgId: number, filter: DashboardFilterRequest): Observable<DashboardMeasureDetailProd[]> {
    return this.http.post<DashboardMipsScoreResponse>(`${this.apiUrl}/dashboard/measures/${orgId}`, filter).pipe(
      map(response => response.measures)
    );
  }

  getDashboardMipsScores(orgId: number, request: DashboardMipsScoreRequest): Observable<DashboardMipsScoreResponse> {
    return this.http.post<DashboardMipsScoreResponse>(`${this.apiUrl}/dashboard/measures/${orgId}`, request);
  }

  getMeasurePatients(orgId: string, measureCode: string, filter: DashboardFilterRequest): Observable<any> {
    return this.http.post<DashboardMeasurePatientProd[]>(`${this.apiUrl}/Dashboard/measures/patients/${orgId}/${measureCode}`, filter);
  }

  getMeasureEvidence(orgId: string, patientID: number, createDate: string, measureCode: string, measureYear: number): Observable<any> {
    return this.http.get<DashboardMeasurePatientProd[]>(`${this.apiUrl}/Dashboard/evidence/${orgId}/${measureCode}/${createDate}/${patientID}/${measureYear}`);
  }

  getDashboardProviders(orgId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/Providers/${orgId}`);
  }

  getDashboardLocations(orgId: number, programYear: number): Observable<any> {
    return this.http.get<DashboardMeasureLocation[]>(`${this.apiUrl}/Dashboard/locations/${orgId}/${programYear}`);
  }

  getDashboardDeciles(year: number): Observable<DashboardMeasureDecile[]> {
    return this.http.get<DashboardMeasureDecile[]>(`${this.apiUrl}/dashboard/deciles/${year}`);
  }

  calculateMipsReweighting(request: CalculateMipsReweightingRequest): Observable<CalculateMipsReweightingResponse> {
    return this.http.post<CalculateMipsReweightingResponse>(`${this.apiUrl}/dashboard/reweighting`, request);
  }

  getMeasureMonthlyTrend(orgId: number, measureCode: string, years: number[]): Observable<any> {
    let params = {
      years: years
    };
    return this.http.post<any[]>(`${this.apiUrl}/Dashboard/measures/${orgId}/${measureCode}/monthly`, params);

  }

  validatePatient(validationRequest: PatientValidationRequest): Observable<PatientValidationResponse> {
  return this.http.post<PatientValidationResponse>(`${this.apiUrl}/Dashboard/measures/patients/validate`, validationRequest);
  }

  removePatientValidation(validationRequest: RemovePatientValidationRequest): Observable<PatientValidationRemovalResponse> {
    return this.http.delete<PatientValidationRemovalResponse>(`${this.apiUrl}/Dashboard/measures/patients/validate`, {
      body: validationRequest
    });
  }

  getMeasureToolPatientData(organizationId: number, patientIDs: string, measureCode: string): Observable<DashboardMeasureToolPatientResult[]> {
    return this.http.get<DashboardMeasureToolPatientResult[]>(`${this.apiUrl}/Dashboard/MeasureToolPatientGet/${organizationId}/${patientIDs}/${measureCode}`);
  }

  getMeasureToolMetaData(measureCode: string): Observable<DashboardMeasureToolMetaDataResult[]> {
    return this.http.get<DashboardMeasureToolMetaDataResult[]>(`${this.apiUrl}/Dashboard/MeasureToolMetaData/${measureCode}`);
  }

  constructor(private http: HttpClient) { }
}
